// Module 09 §8 — dashboard deletion, for both scopes.
//
// Ports `CustomDashboardDeleteModal.vue:38-51` and `AccountCenterDeleteModal.vue:38-52`. What it
// drops from them: the `window.location.reload()` the account-center variant fired after routing
// away (`:50`), and the un-caught promise chain — a failed delete in v3 left the modal open with
// the global loader still spinning, because neither call site had a `.catch`.
//
// Like the save funnel this returns an outcome instead of toasting: `useConfirm` and `useToast`
// are the screen's, and keeping them out is what lets both branches be asserted without mounting.
import type { DashboardApi } from '../api/DashboardApi'
import { dashboardApi } from '../api/DashboardApi'
import type {
  DashboardScope,
  DeleteDashboardInput,
  DeleteDashboardOutcome,
} from '../models/save.types'
import {
  CREATE_DASHBOARD_ROUTE,
  buildAccountCenterRoute,
  buildCustomDashboardRoute,
  type DashboardRouteTarget,
} from '../service/dashboardRoute'
import { useDashboardStore } from '../stores/dashboardStore'

import { ApiError } from '@/shared/http'

export interface DeleteDashboardFlowOptions {
  api?: DashboardApi
}

export class DeleteDashboardFlow {
  private readonly api: DashboardApi

  constructor(options: DeleteDashboardFlowOptions = {}) {
    this.api = options.api ?? dashboardApi
  }

  async execute(input: DeleteDashboardInput): Promise<DeleteDashboardOutcome> {
    const scope = input.scope ?? 'workspace'
    const store = useDashboardStore()

    try {
      if (scope === 'accountCenter') {
        await this.api.removeAccountCenter(input.ownerId, input.id)
        store.removeAccountCenterLocal(input.id)
      } else {
        await this.api.remove(input.ownerId, input.id)
        store.removeLocal(input.id)
      }
    } catch (cause) {
      return {
        status: 'failed',
        error: ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause),
        messageKey: 'dashboards.delete.failed',
      }
    }

    // Only the record on screen forces a route change; deleting from a list leaves you there.
    // Resolved AFTER the local removal, so the target can never point back at the deleted record.
    return {
      status: 'deleted',
      redirectTo: input.isCurrent ? this.nextTarget(scope, input.ownerId) : null,
      messageKey: 'dashboards.delete.deleted',
    }
  }

  /**
   * Where to land once the current record is gone.
   *
   * Dashboards port `CustomDashboardDeleteModal.vue:49` (next dashboard, else the create route).
   * Account centers CANNOT be ported: v3 pushed `{name:'AccountStore'}` and then called
   * `window.location.reload()` (`AccountCenterDeleteModal.vue:50-51`) — that route name has no v4
   * equivalent in module 09's four records, and contract §3.1 drops the reload outright. The bare
   * `AccountCenter` surface is the nearest in-module target, and its `:url?` param is optional
   * precisely so it can be reached without one.
   */
  private nextTarget(scope: DashboardScope, ownerId: number | string): DashboardRouteTarget {
    const store = useDashboardStore()
    if (scope === 'accountCenter') return buildAccountCenterRoute(store.accountCenters[0])
    const next = store.firstDashboard
    return next
      ? buildCustomDashboardRoute(
          { url_id: next.url_id, workspace_id: next.workspace_id },
          { workspaceId: ownerId },
        )
      : CREATE_DASHBOARD_ROUTE
  }
}
